import { t } from '@trimble/i18n';
import { LoadState } from '@trimble/data-loading';
import type {
  ProjectDataNode,
  ProjectDataRepository,
  ProjectDataUnsubscribe,
} from '@trimble/project-data-repository';
import { createProjectDataStatus } from '@trimble/project-data-repository/solid';
import {
  createProjectDataSelection,
  type FilterHandlerRecord,
  useProjectDataPanel,
} from '@trimble/project-data-panel-ui';
import type { FileFolderItem } from '@trimble/project-data-panel-types';
import {
  type ChildrenLoadStatus,
  type MultiSelectProps,
  SelectionTree,
  type SelectionTreeVirtualizationOptions,
  type SingleSelectProps,
  type TreeItemContextMenuOption,
  type TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  type JSX,
  on,
  onCleanup,
  onMount,
  untrack,
} from 'solid-js';
import type { ModelTreeProps as ModelTreeCommonProps } from './ModelTree.types';
import { isPdfModelFile } from './is-pdf-model-file';

const ROOT_NODE_ID = 'tcModels';
const VIEWPORT_LAYOUT_MAX_FRAMES = 4;

type ViewportSize = { readonly width: number; readonly height: number };

function resolveModelTreeVirtualViewport(
  scrollElement: HTMLElement | undefined,
  measuredSize?: ViewportSize
): SelectionTreeVirtualizationOptions['initialRect'] {
  if (measuredSize !== undefined) {
    return measuredSize;
  }

  if (scrollElement === undefined) {
    return undefined;
  }

  const clientWidth = scrollElement.clientWidth;
  const clientHeight = scrollElement.clientHeight;
  if (clientWidth > 0 && clientHeight > 0) {
    return { width: clientWidth, height: clientHeight };
  }

  const { width, height } = scrollElement.getBoundingClientRect();
  if (width > 0 && height > 0) {
    return { width, height };
  }

  return undefined;
}

function readViewportSize(element: HTMLElement): ViewportSize | undefined {
  // Match @tanstack/virtual-core's observeElementRect, which uses offset sizes.
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  if (width > 0 && height > 0) {
    return { width, height };
  }

  const clientWidth = element.clientWidth;
  const clientHeight = element.clientHeight;
  if (clientWidth > 0 && clientHeight > 0) {
    return { width: clientWidth, height: clientHeight };
  }

  const rect = element.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    return { width: rect.width, height: rect.height };
  }

  return undefined;
}

const SUPPORTED_FILE_EXTENSIONS = [
  'dgn',
  'dwg',
  'dxf',
  'ifc',
  'nwc',
  'nwd',
  'rvt',
  'skp',
  'tekla',
  'tflx',
  'trb',
  'ttm',
  'vcl',
];

export interface RepositoryModelTreeProps extends ModelTreeCommonProps {
  readonly repository: ProjectDataRepository;
}

type SearchState =
  | { readonly phase: 'idle'; readonly results: readonly ProjectDataNode[] }
  | { readonly phase: 'loading'; readonly results: readonly ProjectDataNode[] }
  | { readonly phase: 'success'; readonly results: readonly ProjectDataNode[] }
  | { readonly phase: 'error'; readonly results: readonly ProjectDataNode[] };

export const RepositoryModelTree = (
  props: RepositoryModelTreeProps
): JSX.Element => {
  const {
    filterText,
    sortBy,
    sortDirection,
    filterMode,
    setFilterMode,
    panelScrollElement,
    selection: panelSelection,
  } = useProjectDataPanel();
  const repository = untrack(() => props.repository);
  const selection = panelSelection ?? createProjectDataSelection();
  const status = createProjectDataStatus(repository);
  const [scrollViewportSize, setScrollViewportSize] = createSignal<
    ViewportSize | undefined
  >();
  const [revision, setRevision] = createSignal(0);
  const [requestedExpandIds, setRequestedExpandIds] = createSignal<
    readonly string[]
  >([]);
  const [searchState, setSearchState] = createSignal<SearchState>({
    phase: 'idle',
    results: [],
  });
  const parentSubscriptions = new Map<string, ProjectDataUnsubscribe>();
  const expandedFolderIds = new Set<string>();
  let searchController: AbortController | undefined;
  let searchGeneration = 0;
  let observeVisiblePathsGeneration = 0;
  const searchSessionResultIds = new Set<string>();
  let suppressRevisionBump = false;

  const bumpRevision = (): void => {
    if (!suppressRevisionBump) {
      setRevision((current) => current + 1);
    }
  };

  const runBatchedSubscriptionUpdate = (update: () => void): void => {
    suppressRevisionBump = true;
    try {
      update();
    } finally {
      suppressRevisionBump = false;
      setRevision((current) => current + 1);
    }
  };

  const toRepositoryNodeId = (nodeId: string): string =>
    nodeId === ROOT_NODE_ID ? repository.rootId : nodeId;

  const toUiNodeId = (storeNodeId: string): string =>
    storeNodeId === repository.rootId ? ROOT_NODE_ID : storeNodeId;

  const observeAncestorChain = (storeNodeId: string): void => {
    let current = repository.getNode(storeNodeId);
    while (current !== undefined && current.id !== repository.rootId) {
      const parentId = current.parentId;
      expandedFolderIds.add(toUiNodeId(parentId));
      observeChildren(parentId);
      current = repository.getNode(parentId);
    }
    expandedFolderIds.add(ROOT_NODE_ID);
    observeChildren(repository.rootId);
  };

  const observeVisibleModelPaths = (): void => {
    const generation = ++observeVisiblePathsGeneration;
    void (async () => {
      suppressRevisionBump = true;

      try {
        const visit = async (parentId: string): Promise<void> => {
          if (generation !== observeVisiblePathsGeneration) {
            return;
          }

          for (const childId of repository.getChildren(parentId)) {
            if (generation !== observeVisiblePathsGeneration) {
              return;
            }

            const node = repository.getNode(childId);
            if (node === undefined) continue;
            if (node.type === 'FILE') {
              if (props.isNodeVisible(node.id)) {
                if (generation !== observeVisiblePathsGeneration) {
                  return;
                }
                observeAncestorChain(node.id);
              }
              continue;
            }
            if (repository.getChildren(childId).length === 0) {
              if (generation !== observeVisiblePathsGeneration) {
                return;
              }
              observeChildren(childId);
              await repository.ensureChildren(childId);
              if (generation !== observeVisiblePathsGeneration) {
                return;
              }
            }
            await visit(childId);
          }
        };

        await visit(repository.rootId);

        if (generation !== observeVisiblePathsGeneration) {
          return;
        }

        setRevision((current) => current + 1);
      } finally {
        if (generation === observeVisiblePathsGeneration) {
          suppressRevisionBump = false;
        }
      }
    })();
  };

  const observeChildren = (parentId: string): void => {
    if (parentSubscriptions.has(parentId)) return;
    parentSubscriptions.set(
      parentId,
      repository.subscribeChildren(parentId, () =>
        setRevision((current) => current + 1)
      )
    );
    bumpRevision();
  };

  const stopObservingChildren = (parentId: string): void => {
    if (parentId === repository.rootId) return;
    parentSubscriptions.get(parentId)?.();
    parentSubscriptions.delete(parentId);
    bumpRevision();
  };

  const reObserveExpandedDescendants = (storeParentId: string): void => {
    for (const childId of repository.getChildren(storeParentId)) {
      const child = repository.getNode(childId);
      if (child?.type !== 'FOLDER' || !expandedFolderIds.has(childId)) {
        continue;
      }
      observeChildren(childId);
      if (repository.getChildren(childId).length === 0) {
        void repository.ensureChildren(childId);
      }
      reObserveExpandedDescendants(childId);
    }
  };

  onMount(() => observeChildren(repository.rootId));

  createEffect(() => {
    const scrollElement = panelScrollElement?.();
    if (!scrollElement) {
      setScrollViewportSize(undefined);
      return;
    }

    const updateViewportSize = (): void => {
      const nextSize = readViewportSize(scrollElement);
      if (nextSize !== undefined) {
        setScrollViewportSize(nextSize);
      }
    };

    updateViewportSize();

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() => updateViewportSize());
    resizeObserver?.observe(scrollElement);

    let frameChecks = 0;
    let frameId = 0;
    const scheduleFrameCheck = (): void => {
      frameId = requestAnimationFrame(() => {
        updateViewportSize();
        frameChecks += 1;
        if (frameChecks < VIEWPORT_LAYOUT_MAX_FRAMES) {
          scheduleFrameCheck();
        }
      });
    };
    scheduleFrameCheck();

    onCleanup(() => {
      resizeObserver?.disconnect();
      cancelAnimationFrame(frameId);
    });
  });

  createEffect(
    on(filterMode, (mode, previousMode) => {
      if (mode === previousMode) {
        return;
      }

      if (mode !== 'active') {
        ++observeVisiblePathsGeneration;
        suppressRevisionBump = false;
        return;
      }

      observeVisibleModelPaths();
    })
  );

  createEffect(
    on(
      () => status().phase,
      (phase, previousPhase) => {
        if (filterMode() !== 'active') return;
        if (phase !== 'partial' && phase !== 'complete') return;
        if (phase === previousPhase) return;
        observeVisibleModelPaths();
      }
    )
  );

  createEffect(
    on(filterText, (value) => {
      searchController?.abort();
      const query = value.trim();
      const generation = ++searchGeneration;
      if (!query) {
        batch(() => {
          if (props.multiSelect && searchSessionResultIds.size > 0) {
            selection.setSelection(
              [...selection.selectedIds()].filter(
                (nodeId) =>
                  !searchSessionResultIds.has(nodeId) ||
                  repository.getNode(nodeId) !== undefined
              )
            );
          }
          searchSessionResultIds.clear();
          searchController = undefined;
          setSearchState({ phase: 'idle', results: [] });
        });
        return;
      }

      const controller = new AbortController();
      searchController = controller;
      setSearchState({ phase: 'loading', results: [] });
      void repository
        .search(query, controller.signal)
        .then((results) => {
          if (controller.signal.aborted || generation !== searchGeneration) {
            return;
          }
          results.forEach((node) => searchSessionResultIds.add(node.id));
          setSearchState({ phase: 'success', results });
        })
        .catch(() => {
          if (controller.signal.aborted || generation !== searchGeneration) {
            return;
          }
          setSearchState({ phase: 'error', results: [] });
        });
    })
  );

  onCleanup(() => {
    searchController?.abort();
    parentSubscriptions.forEach((unsubscribe) => unsubscribe());
  });

  const toFileFolderItem = (node: ProjectDataNode): FileFolderItem => ({
    ...node,
    loadState: LoadState.NotLoaded,
  });

  const isFileTypeSupported = (fileName: string): boolean => {
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    return fileExtension
      ? SUPPORTED_FILE_EXTENSIONS.includes(fileExtension)
      : false;
  };

  const isModelNodeLoading = (nodeId: string): boolean =>
    nodeId === ROOT_NODE_ID ? false : props.isNodeLoading(nodeId);
  const isModelNodeVisible = (nodeId: string): boolean =>
    nodeId === ROOT_NODE_ID ? false : props.isNodeVisible(nodeId);
  const isImportModal = (): boolean => props.displayMode === 'importModal';

  const bimLazyChildrenEnabled = (): boolean => filterMode() === 'active';

  const lazyExpandAdapter = (): ModelTreeCommonProps['lazyExpand'] =>
    bimLazyChildrenEnabled() ? props.lazyExpand : undefined;

  const requestTreeExpansion = (nodeIds: readonly string[]): void => {
    setRequestedExpandIds(nodeIds);
    queueMicrotask(() => setRequestedExpandIds([]));
  };

  const showModelHierarchy = (item: FileFolderItem): void => {
    batch(() => {
      setFilterMode?.('active');
      requestTreeExpansion([ROOT_NODE_ID, item.id]);
    });
  };

  const findInModelTree = (item: FileFolderItem): void => {
    const ancestorIds = [ROOT_NODE_ID];
    let current = repository.getNode(item.id);
    while (current && current.parentId !== repository.rootId) {
      ancestorIds.push(toUiNodeId(current.parentId));
      current = repository.getNode(current.parentId);
    }
    observeAncestorChain(item.id);
    batch(() => {
      setFilterMode?.('all');
      requestTreeExpansion(ancestorIds);
    });
  };

  const createFileContextMenuOptions = (
    item: FileFolderItem
  ): TreeItemContextMenuOption[] => {
    const options: TreeItemContextMenuOption[] = [];
    // Options are rebuilt with the tree nodes, so hosts must back isNodeVisible
    // with reactive state (as the visibility checkboxes already require).
    const isVisible = isModelNodeVisible(item.id);
    const isLoadFailed = props.isNodeLoadFailed?.(item.id) ?? false;
    const hasSubmodelChildren =
      props.hasNodeSubmodelChildren?.(item.id) ?? false;
    if (props.onRetryLoad && isLoadFailed) {
      options.push({
        text: t('mobileMapping.projectData.retryLoading'),
        onClick: () => props.onRetryLoad?.(item),
      });
    }
    if (
      props.onLoadLatestVersion &&
      isVisible &&
      props.isNodeOldVersion?.(item.id) === true
    ) {
      options.push({
        text: t('mobileMapping.projectData.loadLatestVersion'),
        onClick: () => props.onLoadLatestVersion?.(item),
      });
    }
    if (props.onFitToView && !isLoadFailed && !hasSubmodelChildren) {
      options.push({
        text: t('siteVisualization.sidePanelLayout.fitView'),
        onClick: () => props.onFitToView?.(item),
        disabled: () =>
          !isModelNodeVisible(item.id) || isModelNodeLoading(item.id),
      });
    }
    if (props.onShowOnly && !isLoadFailed && !hasSubmodelChildren) {
      options.push({
        text: t('siteVisualization.sidePanelLayout.showOnly'),
        onClick: () => props.onShowOnly?.(item),
      });
    }
    if (
      props.onEditPosition &&
      isVisible &&
      (props.canEditPosition?.(item) ?? true)
    ) {
      options.push({
        text: t('mobileMapping.projectData.positionSettings'),
        onClick: () => props.onEditPosition?.(item),
      });
    }
    if (props.lazyExpand && isVisible && filterMode() === 'all') {
      options.push({
        text: t('mobileMapping.projectData.modelHierarchy'),
        onClick: () => showModelHierarchy(item),
      });
    }
    if (props.lazyExpand && filterMode() === 'active') {
      options.push({
        text: t('mobileMapping.projectData.findInModelTree'),
        onClick: () => findInModelTree(item),
      });
    }
    if (props.onSeeDetails) {
      options.push({
        text: t('siteVisualization.sidePanelLayout.seeDetails'),
        onClick: () => props.onSeeDetails?.(item),
      });
    }
    if (props.onDownload && (props.canDownload?.(item) ?? true)) {
      options.push({
        text: t('general.download'),
        onClick: () => props.onDownload?.(item),
      });
    }
    if (props.onAddToModelSpace) {
      options.push({
        text:
          props.enablePdfPageImport && isPdfModelFile(item.name)
            ? t('siteVisualization.sidePanelLayout.addPdfPageToModelSpace')
            : t('siteVisualization.sidePanelLayout.addToModelSpace'),
        onClick: () => props.onAddToModelSpace?.(item),
      });
    }
    const validationOptions = props.getValidationContextMenuOptions?.(item);
    if (validationOptions?.length) {
      options.push(...validationOptions);
    }
    const trailingOptions = props.getTrailingContextMenuOptions?.(item);
    if (trailingOptions?.length) {
      options.push(...trailingOptions);
    }
    return options;
  };

  const createFolderContextMenuOptions = (
    item: FileFolderItem
  ): TreeItemContextMenuOption[] => {
    const availability = props.getFolderActionAvailability?.(item);
    const options: TreeItemContextMenuOption[] = [];
    if (props.onFolderLoadAll && availability?.loadAll) {
      options.push({
        text: t('mobileMapping.projectData.loadAll'),
        onClick: () => props.onFolderLoadAll?.(item),
      });
    }
    if (props.onFolderUnloadAll && availability?.unloadAll) {
      options.push({
        text: t('mobileMapping.projectData.unloadAll'),
        onClick: () => props.onFolderUnloadAll?.(item),
      });
    }
    if (props.onFolderCancelLoad && availability?.cancelLoad) {
      options.push({
        text: t('mobileMapping.projectData.cancelLoading'),
        onClick: () => props.onFolderCancelLoad?.(item),
      });
    }
    return options;
  };

  const createItemNode = (
    node: ProjectDataNode,
    searchResult: boolean
  ): TreeNodeDefinition<FileFolderItem> => {
    const item = toFileFolderItem(node);
    const isFolder = item.type === 'FOLDER';
    const validationStatus = isFolder
      ? undefined
      : props.getValidationNodeStatus?.(item);
    const unsupportedStatus =
      !isFolder && !isFileTypeSupported(item.name)
        ? { disabled: true }
        : undefined;
    const importModal = isImportModal();
    const lazyExpand = lazyExpandAdapter();
    const lazyExpandableFile =
      bimLazyChildrenEnabled() &&
      !searchResult &&
      !isFolder &&
      (lazyExpand?.isLazyExpandable?.(item.id) ?? false);
    return {
      id: item.id,
      label: item.name,
      labelIcon: searchResult && isFolder ? 'folder-closed' : undefined,
      data: item,
      parentId:
        searchResult || item.parentId === repository.rootId
          ? ROOT_NODE_ID
          : item.parentId,
      isLeaf: searchResult || (!isFolder && !lazyExpandableFile),
      allowSelectFolder: lazyExpandableFile || (importModal && isFolder),
      showVisibilityToggle: importModal ? false : isFolder ? false : true,
      status: validationStatus ?? unsupportedStatus,
      toggleTooltip: isFolder
        ? undefined
        : t('mobileMapping.projectData.toggleModelVisibility'),
      contextMenuOptions: importModal
        ? undefined
        : isFolder
          ? createFolderContextMenuOptions(item)
          : createFileContextMenuOptions(item),
      visibleContent: (() => {
        const getTrailing = props.getItemTrailingContent;
        return getTrailing ? () => getTrailing(item) ?? null : undefined;
      })(),
    };
  };

  const rootNode = (): TreeNodeDefinition<FileFolderItem> => ({
    id: ROOT_NODE_ID,
    label: t('mobileMapping.projectData.models'),
    labelIcon: 'cube',
    isLeaf: false,
    allowSelectFolder: false,
  });

  const lazyExpandVisibilityFingerprint = createMemo((): string => {
    if (!props.lazyExpand || !bimLazyChildrenEnabled()) {
      return '';
    }
    const parts: string[] = [];
    parentSubscriptions.forEach((_unsubscribe, parentId) => {
      repository.getChildren(parentId).forEach((nodeId) => {
        const node = repository.getNode(nodeId);
        if (node?.type !== 'FILE') {
          return;
        }
        parts.push(
          `${nodeId}:${props.isNodeVisible(nodeId) ? 'v' : 'h'}:${props.isNodeLoading(nodeId) ? 'l' : '-'}`
        );
      });
    });
    return parts.join('|');
  });

  createEffect(
    on(filterMode, (mode, previousMode) => {
      if (mode === previousMode) {
        return;
      }
      if (mode === 'all') {
        observeChildren(repository.rootId);
      }
      setRevision((current) => current + 1);
    })
  );

  const treeNodes = createMemo((): TreeNodeDefinition<FileFolderItem>[] => {
    revision();
    lazyExpandVisibilityFingerprint();
    const currentSearch = searchState();
    if (filterText().trim()) {
      return [
        rootNode(),
        ...currentSearch.results.map((node) => createItemNode(node, true)),
      ];
    }

    const nodes: TreeNodeDefinition<FileFolderItem>[] = [rootNode()];
    const seen = new Set<string>();
    parentSubscriptions.forEach((_unsubscribe, parentId) => {
      repository.getChildren(parentId).forEach((nodeId) => {
        if (seen.has(nodeId)) return;
        const node = repository.getNode(nodeId);
        if (!node) return;
        seen.add(nodeId);
        nodes.push(createItemNode(node, false));
      });
    });
    if (bimLazyChildrenEnabled()) {
      for (const node of lazyExpandAdapter()?.getCachedNodes?.() ?? []) {
        if (!seen.has(node.id)) {
          seen.add(node.id);
          nodes.push(node);
        }
      }
    }
    return nodes;
  });

  const childrenLoadStatus = createMemo<
    ChildrenLoadStatus['childrenLoadStatus']
  >(() => {
    const currentSearch = searchState();
    if (filterText().trim()) {
      if (currentSearch.phase === 'error') return 'error';
      return currentSearch.phase === 'success' ? 'success' : 'pending';
    }
    if (treeNodes().length > 1) return 'success';
    if (status().phase === 'error') return 'error';
    return status().phase === 'partial' || status().phase === 'complete'
      ? 'success'
      : 'pending';
  });

  const defaultExpandIds = createMemo(() => {
    revision();
    return [
      ...new Set([
        ...(props.defaultExpandIds ?? []),
        ...(lazyExpandAdapter()?.getExpandedNodeIds?.() ?? []),
        ...requestedExpandIds(),
      ]),
    ];
  });

  const ownsModelNode = (nodeId: string): boolean => {
    if (nodeId === ROOT_NODE_ID) return false;
    if (lazyExpandAdapter()?.ownsLazyNode?.(nodeId)) {
      return true;
    }
    revision();
    if (repository.getNode(nodeId) !== undefined) return true;
    if (filterText().trim()) {
      return searchState().results.some((node) => node.id === nodeId);
    }
    return false;
  };
  const isModelNodeDisabled = (nodeId: string): boolean => {
    const node =
      searchState().results.find((result) => result.id === nodeId) ??
      repository.getNode(nodeId);
    return (
      node !== undefined &&
      node.type !== 'FOLDER' &&
      !isFileTypeSupported(node.name)
    );
  };

  const sectionHandle = selection.registerSection({
    ownsNode: ownsModelNode,
    isNodeVisible: isModelNodeVisible,
    onVisibilityToggle: (nodeId) => props.onVisibilityToggle(nodeId),
    isNodeDisabled: isModelNodeDisabled,
    isNodeLoading: isModelNodeLoading,
  });
  onCleanup(() => sectionHandle.dispose());

  const selectionProps = createMemo<
    SingleSelectProps<FileFolderItem> | MultiSelectProps
  >(() =>
    props.multiSelect
      ? {
          multiSelect: true,
          isNodeSelected: selection.isNodeSelected,
          onSelectionChange: (ids) => sectionHandle.applySelection(ids),
        }
      : {
          multiSelect: false,
          isNodeSelected: props.isNodeSelected,
          onNodeSelect: props.onNodeSelect,
        }
  );

  const filterHandlers: FilterHandlerRecord<FileFolderItem> = {
    all: { filter: () => true },
    active: {
      filter: (node) =>
        lazyExpandAdapter()?.ownsLazyNode?.(node.id)
          ? (lazyExpandAdapter()?.isLazyNodeVisible?.(node.id) ?? true)
          : isModelNodeVisible(node.id),
    },
    archived: { filter: () => false },
  };

  const toggleModelVisibility = (nodeId: string): void => {
    if (nodeId === ROOT_NODE_ID) return;
    if (props.multiSelect) selection.onVisibilityToggle(nodeId);
    else props.onVisibilityToggle(nodeId);
  };

  const stopObservingDescendants = (parentId: string): void => {
    for (const childId of repository.getChildren(parentId)) {
      const child = repository.getNode(childId);
      if (child?.type !== 'FOLDER') continue;
      stopObservingDescendants(childId);
      stopObservingChildren(childId);
    }
  };

  const handleNodeExpand = (nodeId: string): void => {
    if (filterText().trim()) return;
    const lazyExpand = lazyExpandAdapter();
    if (
      lazyExpand &&
      (lazyExpand.ownsLazyNode?.(nodeId) ||
        lazyExpand.isLazyExpandable?.(nodeId))
    ) {
      lazyExpand.onNodeExpand?.(nodeId);
      setRevision((current) => current + 1);
      return;
    }
    const storeNodeId = toRepositoryNodeId(nodeId);
    const node =
      nodeId === ROOT_NODE_ID ? undefined : repository.getNode(storeNodeId);
    if (nodeId !== ROOT_NODE_ID && node?.type !== 'FOLDER') return;

    runBatchedSubscriptionUpdate(() => {
      expandedFolderIds.add(nodeId);
      observeChildren(storeNodeId);
      if (repository.getChildren(storeNodeId).length === 0) {
        void repository.ensureChildren(storeNodeId);
      }
      reObserveExpandedDescendants(storeNodeId);
    });
  };

  const handleNodeCollapse = (nodeId: string): void => {
    lazyExpandAdapter()?.onNodeCollapse?.(nodeId);
    runBatchedSubscriptionUpdate(() => {
      expandedFolderIds.delete(nodeId);

      // Active/archived filters still need observed nodes after a UI collapse.
      if (filterMode() !== 'all') {
        return;
      }

      const storeNodeId = toRepositoryNodeId(nodeId);
      if (nodeId === ROOT_NODE_ID) {
        for (const childId of repository.getChildren(repository.rootId)) {
          stopObservingDescendants(childId);
          stopObservingChildren(childId);
        }
        return;
      }

      stopObservingDescendants(storeNodeId);
      stopObservingChildren(storeNodeId);
    });
  };

  const lazySelectionTreeProps = createMemo(() => {
    const lazyExpand = lazyExpandAdapter();
    if (!lazyExpand) {
      return { lazyChildrenEnabled: bimLazyChildrenEnabled() };
    }
    return {
      lazyChildrenEnabled: true,
      loadChildren: (nodeId: string) => lazyExpand.loadChildren(nodeId),
      loadChildrenAtomically: (nodeId: string) =>
        lazyExpand.loadChildrenAtomically?.(nodeId) ?? false,
    };
  });

  const modelTreeVirtualization = createMemo(
    (): SelectionTreeVirtualizationOptions | undefined => {
      const scrollElement = panelScrollElement?.();
      const initialRect = resolveModelTreeVirtualViewport(
        scrollElement,
        scrollViewportSize()
      );
      if (!scrollElement || !initialRect) {
        return undefined;
      }

      return {
        getScrollElement: () => scrollElement,
        stickyNodeIds: [ROOT_NODE_ID],
        initialRect,
      };
    }
  );

  return (
    <div class="w-full" data-testid="model-selection-tree">
      <SelectionTree
        nodes={treeNodes()}
        childrenLoadStatus={childrenLoadStatus()}
        visibleContent={props.visibleContent}
        rowMode={props.rowMode}
        sortBy={sortBy()}
        sortDirection={sortDirection()}
        filterText={filterText().trim() ? '' : filterText()}
        highlightText={filterText()}
        filterMode={filterMode()}
        filterHandlers={filterHandlers}
        defaultExpandIds={defaultExpandIds()}
        virtualization={modelTreeVirtualization()}
        isNodeLoading={isModelNodeLoading}
        isNodeVisible={isModelNodeVisible}
        onVisibilityToggle={toggleModelVisibility}
        onCancelVisibilityToggle={(nodeId) => {
          if (nodeId !== ROOT_NODE_ID) {
            props.onCancelVisibilityToggle?.(nodeId);
          }
        }}
        onNodeExpand={handleNodeExpand}
        onNodeCollapse={handleNodeCollapse}
        showCheckbox={isImportModal()}
        cascadeParentSelection={isImportModal()}
        {...lazySelectionTreeProps()}
        {...selectionProps()}
      />
    </div>
  );
};
